import { useCallback, useEffect, useRef, useState } from "react";
import {
  AppOrderItem,
  AppOrderList,
  AppOrderTitle,
  AppOrderTitleToggle,
  AppOrderTitleToggleCircle,
  AppOrderToggleLabel,
  AppOrderWrapper,
} from "./styles";
import { io } from "socket.io-client";
import { Socket } from "socket.io-client/build/esm/socket";
import { pubState } from "../../../../recoil/pub.atom";
import { useRecoilValue } from "recoil";
import { formatDate } from "../../../../utils/date";

export interface AppOrderResponse {
  id: number;
  nickname: string;
  memo: string;
  table: string;
  seat: number;
  isPickUp: boolean;
  createdAt: string;
  appOrderMenus: AppOrderMenu[];
}

export interface AppOrderMenu {
  id: number;
  appOrderId: number;
  productName: string;
  productPrice: number;
  count: number;
}

const AppOrder = () => {
  const socketRef = useRef<Socket | null>(null);
  const pub = useRecoilValue(pubState);

  const [isTtsPlaying, setIsTtsPlaying] = useState(true);
  const isTtsPlayingRef = useRef(isTtsPlaying);
  const ttsQueueRef = useRef<string[]>([]);
  const isTtsSpeakingRef = useRef(false);
  const processedOrderIdsRef = useRef<Set<number>>(new Set());
  const [orderList, setOrderList] = useState<AppOrderResponse[]>([]);

  // isTtsPlaying이 변경될 때마다 ref 업데이트
  useEffect(() => {
    isTtsPlayingRef.current = isTtsPlaying;
  }, [isTtsPlaying]);

  // 브라우저 알림 권한 요청
  const requestNotificationPermission = async () => {
    if (!("Notification" in window)) {
      return false;
    }

    // 이미 권한이 허용된 경우
    if (Notification.permission === "granted") {
      return true;
    }

    // 권한이 거부된 경우
    if (Notification.permission === "denied") {
      alert(
        "알림 권한이 거부되어 있습니다.\n브라우저 설정에서 알림 권한을 허용해주세요.\n\n크롬: 주소창 왼쪽 아이콘 > 알림 > 허용"
      );
      return false;
    }

    // 권한이 아직 요청되지 않은 경우 (default)
    const permission = await Notification.requestPermission();
    return permission === "granted";
  };

  // 크롬 알림 표시
  const showNotification = (order: AppOrderResponse) => {
    if (!("Notification" in window)) {
      console.log("이 브라우저는 알림을 지원하지 않습니다.");
      return;
    }

    const menuText = order.appOrderMenus
      .map((menu) => `${menu.productName} x ${menu.count}`)
      .join(", ");

    const locationInfo = order.isPickUp
      ? "픽업"
      : `테이블: ${order.table} 좌석: ${order.seat}`;

    const notification = new Notification("새 주문이 들어왔습니다!", {
      body: `닉네임: ${
        order.nickname
      }\n${locationInfo}\n메뉴: ${menuText}\n요청사항: ${order.memo || "없음"}`,
      icon: "/image-web/logo/logo.png", // 아이콘 경로 (필요시 수정)
      badge: "/image-web/logo/logo.png",
      tag: `order-${order.id}`, // 같은 주문의 중복 알림 방지
      requireInteraction: true, // 사용자가 직접 닫을 때까지 유지
    });

    // 알림 클릭 시 포커스
    notification.onclick = () => {
      window.focus();
      notification.close();
    };
  };

  // TTS 큐에서 다음 항목 재생
  const playNextTTS = useCallback(() => {
    if (ttsQueueRef.current.length === 0) {
      isTtsSpeakingRef.current = false;
      return;
    }

    if (!("speechSynthesis" in window)) {
      console.log("TTS를 지원하지 않는 브라우저입니다.");
      ttsQueueRef.current = [];
      isTtsSpeakingRef.current = false;
      return;
    }

    const text = ttsQueueRef.current.shift();
    if (!text) {
      isTtsSpeakingRef.current = false;
      return;
    }

    isTtsSpeakingRef.current = true;

    const utterance = new SpeechSynthesisUtterance(text);

    // TTS 재생 완료 시 다음 큐 아이템 재생
    utterance.onend = () => {
      playNextTTS();
    };

    utterance.onerror = () => {
      playNextTTS();
    };

    // 보이스 선택 함수
    const selectVoice = () => {
      const voices = window.speechSynthesis.getVoices();

      if (voices.length === 0) {
        // 보이스가 아직 로드되지 않은 경우, 이벤트 리스너로 대기
        window.speechSynthesis.onvoiceschanged = () => {
          const loadedVoices = window.speechSynthesis.getVoices();
          setVoiceForUtterance(loadedVoices);
        };
        return;
      }

      setVoiceForUtterance(voices);
    };

    const setVoiceForUtterance = (voices: SpeechSynthesisVoice[]) => {
      const targetVoice = voices.find((voice) => {
        return voice.name.includes("Google 한국의");
      });

      if (targetVoice) {
        utterance.voice = targetVoice;
      } else {
        // 대체 보이스 선택
        const fallbackVoice = voices.find((voice) => {
          return voice.lang.startsWith("ko");
        });
        if (fallbackVoice) {
          utterance.voice = fallbackVoice;
        }
      }

      utterance.lang = "ko-KR";
      utterance.rate = 1.0;
      utterance.pitch = 1.0;
      utterance.volume = 1.0;

      window.speechSynthesis.speak(utterance);
    };

    selectVoice();
  }, []);

  // TTS 재생 함수 (큐에 추가)
  const playTTS = useCallback(
    (text: string) => {
      if (!isTtsPlayingRef.current) return;

      // 큐에 추가
      ttsQueueRef.current.push(text);

      // 현재 재생 중이 아니면 바로 재생 시작
      if (!isTtsSpeakingRef.current) {
        playNextTTS();
      }
    },
    [playNextTTS]
  );

  // 소켓 연결
  const connectSocket = useCallback(() => {
    if (!pub) return;
    if (socketRef.current?.connected) return; // 이미 연결되어있다면 return

    // 혹시 남아있는 소켓 제거
    if (socketRef.current) {
      socketRef.current.disconnect();
      socketRef.current = null;
    }

    socketRef.current = io(`${process.env.REACT_APP_WEB_HOST}/order`, {
      transports: ["websocket"],
      path: "/socket.io/",
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
    });

    // 방 참여
    socketRef.current.emit("join-order-room", { pubId: pub.id });

    socketRef.current?.on("order-list", (result: AppOrderResponse[]) => {
      setOrderList(result);
    });

    socketRef.current?.on(
      "new-order-list",
      async (result: AppOrderResponse[]) => {
        setOrderList(result);

        // 배열의 첫 번째 항목만 새로운 주문
        if (result && result.length > 0) {
          const newOrder = result[0];

          // 이미 처리된 주문이면 무시
          if (processedOrderIdsRef.current.has(newOrder.id)) {
            return;
          }

          // 처리된 주문 목록에 추가
          processedOrderIdsRef.current.add(newOrder.id);

          // 권한이 이미 허용된 경우 바로 알림 표시
          if (Notification.permission === "granted") {
            showNotification(newOrder);
          } else {
            // 권한이 없거나 거부된 경우 권한 요청
            const hasPermission = await requestNotificationPermission();
            if (hasPermission) {
              showNotification(newOrder);
            }
          }

          // TTS 재생
          if (isTtsPlayingRef.current) {
            const menuText = newOrder.appOrderMenus
              .map((menu) => `${menu.productName} ${menu.count}개`)
              .join(", ");

            const locationInfo = newOrder.isPickUp
              ? "직접 픽업"
              : `${newOrder.table}테이블 ${newOrder.seat}번`;

            const text = `${
              newOrder.nickname
            }님\n${locationInfo} 으로\n${menuText} 주문\n요청사항 ${
              newOrder.memo || "없음"
            }`;

            playTTS(text);
          }
        }
      }
    );
  }, [pub, playTTS]);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const leaveSocket = () => {
    if (!socketRef.current || !pub) return;
    socketRef.current.emit("leave-order-room", { pubId: pub.id });
  };

  const disconnectSocket = () => {
    if (socketRef.current) {
      socketRef.current.disconnect();
      socketRef.current = null;
    }
  };

  useEffect(() => {
    // 컴포넌트 마운트 시 알림 권한 요청
    requestNotificationPermission();

    connectSocket();

    const interval = setInterval(() => {
      if (socketRef.current && !socketRef.current.connected) {
        connectSocket();
      }
    }, 1000);

    return () => {
      clearInterval(interval);
      disconnectSocket();
    };
  }, [connectSocket]);
  return (
    <AppOrderWrapper>
      <AppOrderTitle>
        실시간 주문 내역
        <AppOrderToggleLabel>새 주문 읽어 주기</AppOrderToggleLabel>
        <AppOrderTitleToggle
          isOn={isTtsPlaying}
          onClick={() => setIsTtsPlaying(!isTtsPlaying)}
        >
          <AppOrderTitleToggleCircle isOn={isTtsPlaying} />
        </AppOrderTitleToggle>
      </AppOrderTitle>
      <AppOrderList>
        {orderList.map((item) => (
          <AppOrderItem key={item.id}>
            주문번호: {item.id} <br />
            일시 : {formatDate(item.createdAt, true)} <br />
            닉네임 : {item.nickname}, {item.isPickUp ? "픽업" : ""}{" "}
            {!item.isPickUp && `테이블: ${item.table} 좌석: ${item.seat}`}
            <br />
            메뉴 :{" "}
            {item.appOrderMenus
              .map((menu) => `${menu.productName} x ${menu.count}`)
              .join(", ")}
            <br />
            요청사항 : {item.memo}
          </AppOrderItem>
        ))}
      </AppOrderList>
    </AppOrderWrapper>
  );
};

export default AppOrder;
